import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// НЕ задаём Content-Type здесь. В axios 1.x default Content-Type
// application/json ломает FormData-аплоады: трансформер видит
// data=FormData + Content-Type=application/json и конвертирует FormData
// в JSON через formDataToJSON. На бэке multer получает application/json
// без multipart-boundary → req.files пустой → 400 «No files uploaded»
// («ничего не появляется» / «не удалось загрузить» — глазами юзера на
// help.recore.su). Axios сам выставит правильный Content-Type per request:
// application/json для объектов, multipart/form-data; boundary=... для
// FormData.
const apiClient = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
});

interface QueuedRequest {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}

let isRefreshing = false;
let failedQueue: QueuedRequest[] = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor: attach Bearer token + a "client portal" marker.
// X-Portal: client заставляет бэкенд жёстко ограничивать выдачу до
// тикетов/данных текущего пользователя, даже если у аккаунта роль admin
// (тестовые админ-токены — оператор не должен из своего личного кабинета
// видеть переписки клиентов).
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    if (config.headers) {
      config.headers['X-Portal'] = 'client';
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: 401 refresh logic
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post('/api/v1/auth/refresh', {
          refreshToken,
        });

        const responseData = data.data || data;
        const newAccessToken = responseData.tokens?.accessToken || responseData.accessToken;
        const newRefreshToken = responseData.tokens?.refreshToken || responseData.refreshToken;

        localStorage.setItem('accessToken', newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        processQueue(null, newAccessToken);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
